import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Table, Spinner, Button } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

const C = {
  navy:    '#0f172a',
  navy2:   '#1e293b',
  blue:    '#2563eb',
  sky:     '#0ea5e9',
  skyD:    '#0369a1',
  teal:    '#0d9488',
  tealD:   '#0f766e',
  tealHdr: '#0e7490',
  green:   '#10b981',
  greenD:  '#059669',
  amber:   '#f59e0b',
  amberD:  '#d97706',
  red:     '#ef4444',
  redD:    '#dc2626',
  redHdr:  '#991b1b',
  slate:   '#f8fafc',
  border:  '#cbd5e1',
  borderD: '#94a3b8',
  text:    '#1e293b',
  muted:   '#64748b',
  light:   '#f1f5f9',
  yellowInput: '#fef08a'
};

const fmt = (v) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(v || 0);

const fmtNum = (v) =>
  new Intl.NumberFormat('id-ID').format(v || 0);

const Pill = ({ bg, color, children, outline }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '1px 6px',
    borderRadius: 12, fontSize: '0.66rem', lineHeight: 1.2, fontWeight: 600,
    background: outline ? 'transparent' : (bg || '#f1f5f9'),
    color: color || C.muted,
    border: outline ? `1px solid ${bg || C.border}` : 'none',
    whiteSpace: 'nowrap'
  }}>
    {children}
  </span>
);

const ProsesPesananApotek = () => {
  const navigate = useNavigate();
  const locationState = useLocation();

  // Ambil lokasi dari state atau default ke '00001' (Apotek)
  const defaultLokasi = locationState.state?.lokasi || '00001';
  const [lokasi] = useState(defaultLokasi);

  // Supplier state
  const [supplierList, setSupplierList] = useState([]);
  const [selectedKdSp, setSelectedKdSp] = useState('');
  const [inputKdSp, setInputKdSp] = useState('');
  const [selectedNmSp, setSelectedNmSp] = useState('');
  const [minOrder, setMinOrder] = useState(0);
  const [totalPesanan, setTotalPesanan] = useState(0);
  const [kurangOrder, setKurangOrder] = useState(0);

  // DOM Refs untuk kontrol fokus keyboard sesuai Gambas
  const inputKdSpRef = useRef(null);
  const inputTglKirimRef = useRef(null);

  // Tanggal kirim (default: hari ini)
  const todayStr = () => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };
  const [tglKirim, setTglKirim] = useState(todayStr());

  // Data items pesanan TMPSP untuk supplier terpilih (Tabelharga2)
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');

  // Modal Supplier Selector (FrmQuery)
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [searchSupplier, setSearchSupplier] = useState('');

  // Modal Konfirmasi Proses SP
  const [showConfirmProses, setShowConfirmProses] = useState(false);
  const [isProcessingSp, setIsProcessingSp] = useState(false);

  // Modal PDF Viewer (Draft & SP Resmi)
  const [showModalPdf, setShowModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('Surat Pesanan');
  const [pdfFilename, setPdfFilename] = useState('Surat_Pesanan.pdf');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Modal Cetak Ulang SP
  const [showCetakUlangModal, setShowCetakUlangModal] = useState(false);
  const [historySpList, setHistorySpList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState('');
  const [inputNoSpUlang, setInputNoSpUlang] = useState('');

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (message, variant = 'success') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // 1. PUBLIC SUB show_data2(kdspToFetch, nmspToFetch)
  // Menampilkan isi tabel Tabelharga2 untuk pemasok terpilih dari TMPSP:
  // Select NM_OBAT,PRINSIPAL,MINORDER,QTYBELI,SATUANBELI,KONVERSI,SATUANJUAL,PRICELIST,DISC,DISCRP,HRGJADI 
  // from TMPSP where lokasi = ... and (kdsp = ... or nmsp = ...) and flag = 'V' and qty > 0 order by FLAG DESC, NM_OBAT
  const show_data2 = useCallback(async (kdspToFetch, nmspToFetch) => {
    const spKey = kdspToFetch !== undefined ? kdspToFetch : selectedKdSp;
    const nmKey = nmspToFetch !== undefined ? nmspToFetch : selectedNmSp;
    if (!spKey && !nmKey) {
      setItems([]);
      return;
    }

    setLoadingItems(true);
    try {
      const url = `${getApiUrl()}/show_pesanan_suplier?lokasi=${lokasi}&kdsp=${encodeURIComponent(spKey || '')}&nmsp=${encodeURIComponent(nmKey || '')}&search=${encodeURIComponent(filterQuery)}`;
      const res = await fetch(url);
      const j = await res.json();
      if (j.status === 1) {
        const data = j.data || [];
        setItems(data);

        if (j.suplier) {
          setSelectedKdSp(j.suplier.kdsp || spKey || '');
          setInputKdSp(j.suplier.kdsp || spKey || '');
          setSelectedNmSp(j.suplier.nmsp || nmKey || '');
          setMinOrder(j.suplier.minorder || 0);
          setTotalPesanan(j.suplier.total_pesanan || 0);
          setKurangOrder(j.suplier.kurang || 0);
        }
      } else {
        setItems([]);
      }
    } catch (e) {
      console.error('Error in show_data2:', e);
      showToast('Gagal memuat item pesanan: ' + e.message, 'danger');
    } finally {
      setLoadingItems(false);
    }
  }, [lokasi, selectedKdSp, selectedNmSp, filterQuery]);

  useEffect(() => {
    if (selectedKdSp || selectedNmSp) {
      show_data2(selectedKdSp, selectedNmSp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  // Handler memilih supplier dari windows dialog / list
  const selectSupplier = useCallback((sup) => {
    if (!sup) return;
    const targetKdSp = sup.kdsp || '';
    const targetNmSp = sup.nmsp || '';

    setSelectedKdSp(targetKdSp);
    setInputKdSp(targetKdSp);
    setSelectedNmSp(targetNmSp);
    setMinOrder(sup.minorder || 0);
    setTotalPesanan(sup.total_pesanan || 0);
    setKurangOrder(sup.kurang || 0);
    setShowSupplierModal(false);

    show_data2(targetKdSp, targetNmSp);

    // Fokus ke tanggal pengiriman sesuai alur Gambas (txttglkirim.SetFocus)
    setTimeout(() => {
      inputTglKirimRef.current?.focus();
    }, 150);
  }, [show_data2]);

  // 2. Fetch daftar supplier yang memiliki pesanan di TMPSP dari endpoint /daftar_suplier_sp
  const fetchSuppliers = useCallback(async () => {
    setLoadingSuppliers(true);
    try {
      const res = await fetch(`${getApiUrl()}/daftar_suplier_sp?lokasi=${lokasi}`);
      const j = await res.json();
      if (j.status === 1) {
        const list = j.data || [];
        setSupplierList(list);
      }
    } catch (e) {
      console.error('Error fetching suppliers:', e);
      showToast('Gagal memuat daftar pemasok dari server: ' + e.message, 'danger');
    } finally {
      setLoadingSuppliers(false);
    }
  }, [lokasi]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // PUBLIC SUB txtkdsuplier_KeyPress()
  // Jika Return (Enter): cari di TMPSP, jika ada isi nama & tampilkan tabel (show_data2) lalu fokus tgl kirim,
  // jika belum cocok: buka windows dialog FrmQuery untuk mencari supplier
  // Jika Esc: kembali ke modul SuratPesananApotek (Panelhitung.Visible = TRUE)
  const handleKdSpKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = (inputKdSp || '').trim().toLowerCase();
      if (!val) {
        setSearchSupplier('');
        setShowSupplierModal(true);
        return;
      }

      // 1. Cek exact match pada kode supplier
      const exactCodeMatch = supplierList.find(s => (s.kdsp || '').toLowerCase() === val);
      if (exactCodeMatch) {
        selectSupplier(exactCodeMatch);
        return;
      }

      // 2. Cek exact match pada nama supplier
      const exactNameMatch = supplierList.find(s => (s.nmsp || '').toLowerCase() === val);
      if (exactNameMatch) {
        selectSupplier(exactNameMatch);
        return;
      }

      // 3. Cek partial match nama supplier
      const partialMatches = supplierList.filter(s =>
        (s.nmsp || '').toLowerCase().includes(val) || (s.kdsp || '').toLowerCase().includes(val)
      );
      if (partialMatches.length === 1) {
        selectSupplier(partialMatches[0]);
      } else {
        // Buka windows dialog pemilih pemasok (FrmQuery) dengan kata kunci pencarian
        setSearchSupplier(inputKdSp || '');
        setShowSupplierModal(true);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleKeluar();
    }
  };

  // 3. Tombol DRAFT (Pratinjau Cetak Draft SP)
  const handlePrintDraft = async () => {
    if (!selectedKdSp) {
      showToast('Silakan pilih Pemasok terlebih dahulu', 'warning');
      return;
    }
    if (items.length === 0) {
      showToast('Tidak ada item pesanan untuk pemasok ini', 'warning');
      return;
    }

    setIsGeneratingPdf(true);
    setPdfTitle(`DRAFT SURAT PESANAN: ${selectedNmSp}`);
    setPdfFilename(`DRAFT_SP_${selectedKdSp}.pdf`);
    setShowModalPdf(true);
    setPdfUrl('');

    try {
      const res = await fetch(
        `${getApiUrl()}/cetak_sp_draft?lokasi=${lokasi}&kdsp=${encodeURIComponent(selectedKdSp || '')}&nmsp=${encodeURIComponent(selectedNmSp || '')}&tgl_kirim=${encodeURIComponent(tglKirim)}`
      );
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.msg || 'Gagal menghasilkan draft SP');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (e) {
      setShowModalPdf(false);
      showToast('Kesalahan cetak draft: ' + e.message, 'danger');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // 4. Tombol PROSES (Buka Konfirmasi Simpan SP Resmi)
  const handleOpenConfirmProses = () => {
    if (!selectedKdSp && !selectedNmSp) {
      showToast('Silakan pilih Pemasok terlebih dahulu', 'warning');
      return;
    }
    if (items.length === 0) {
      showToast('Tidak ada item pesanan untuk pemasok ini', 'warning');
      return;
    }
    setShowConfirmProses(true);
  };

  // 5. Eksekusi Simpan SP Resmi
  const handleExecuteProses = async () => {
    setShowConfirmProses(false);
    setIsProcessingSp(true);
    setIsGeneratingPdf(true);
    setPdfTitle('Menyiapkan Surat Pesanan...');
    setShowModalPdf(true);
    setPdfUrl('');

    try {
      const res = await fetch(`${getApiUrl()}/proses_sp_suplier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lokasi,
          kdsp: selectedKdSp || '',
          nmsp: selectedNmSp || '',
          tgl_kirim: tglKirim
        })
      });
      const j = await res.json();
      if (!res.ok || j.status !== 1) {
        throw new Error(j.msg || 'Gagal memproses Surat Pesanan');
      }

      const noSpResmi = j.no_sp;
      showToast(j.msg || `Surat Pesanan ${noSpResmi} berhasil dibuat!`, 'success');
      setPdfTitle(`Surat Pesanan Resmi: ${noSpResmi}`);
      setPdfFilename(`SP_${noSpResmi.replace(/\//g, '_')}.pdf`);

      // Ambil dokumen PDF SP Resmi
      const pdfRes = await fetch(`${getApiUrl()}/cetak_sp_pdf?no_sp=${encodeURIComponent(noSpResmi)}`);
      if (pdfRes.ok) {
        const blob = await pdfRes.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }

      // Reset selection and refresh suppliers
      setSelectedKdSp('');
      setSelectedNmSp('');
      setMinOrder(0);
      setTotalPesanan(0);
      setKurangOrder(0);
      setItems([]);
      fetchSuppliers();

    } catch (e) {
      setShowModalPdf(false);
      showToast('Gagal memproses SP: ' + e.message, 'danger');
    } finally {
      setIsProcessingSp(false);
      setIsGeneratingPdf(false);
    }
  };

  // 6. Cetak Ulang Handler Sesuai Script VB Gambas
  const openCetakUlangModal = async () => {
    setShowCetakUlangModal(true);
    setInputNoSpUlang('');
    setLoadingHistory(true);
    try {
      const res = await fetch(`${getApiUrl()}/list_sp_history?lokasi=${lokasi}`);
      const j = await res.json();
      if (j.status === 1) {
        setHistorySpList(j.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCetakSpByNo = async (noSpInput) => {
    const spNumber = (noSpInput || inputNoSpUlang || '').trim();
    if (!spNumber) {
      showToast('Silakan masukkan nomor Surat Pesanan yang akan dicetak', 'warning');
      return;
    }

    try {
      // 1. Verifikasi data HDSP sesuai VB Gambas:
      // sql = "select * from HDSP where lokasi = '" & micram.xid & "'and no_sp = '" & txtnospulang.Text & "'"
      // rscek = mkoneksiapot.db.Exec(sql)
      const res = await fetch(`${getApiUrl()}/cetak_ulang_sp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lokasi, no_sp: spNumber })
      });
      const j = await res.json();

      // IF rscek.Available = FALSE THEN
      //   message.Warning("Nomor SP tidak terdaftar")
      //   panelulang.Visible = FALSE
      if (!res.ok || j.status === 0) {
        showToast(j.msg || 'Nomor SP tidak terdaftar', 'warning');
        setShowCetakUlangModal(false);
        return;
      }

      // IF rscek.Available = TRUE THEN
      //   txtkdsuplier.Text = rscek!kd_suplier
      //   txttglkirim.Text = rscek!tgl_kirim
      //   printsp(txtnospulang.Text, rscek!tanggal)
      const data = j.data || {};
      if (data.kd_suplier) {
        setInputKdSp(data.kd_suplier);
        setSelectedKdSp(data.kd_suplier);
      }
      if (data.nm_suplier) {
        setSelectedNmSp(data.nm_suplier);
      }
      if (data.tgl_kirim) {
        setTglKirim(data.tgl_kirim);
      }

      // Sesuai VB: panelulang.Visible = FALSE -> tutup modal input cetak ulang
      setShowCetakUlangModal(false);

      // Sesuai VB: printsp(txtnospulang.Text, rscek!tanggal)
      setIsGeneratingPdf(true);
      setPdfTitle(`SURAT PESANAN OBAT DAN ALAT KESEHATAN APOTEK - ${spNumber}`);
      setPdfFilename(`SP_${spNumber.replace(/\//g, '_')}.pdf`);
      setShowModalPdf(true);
      setPdfUrl('');

      const pdfRes = await fetch(`${getApiUrl()}/cetak_sp_pdf?no_sp=${encodeURIComponent(spNumber)}&lokasi=${lokasi}`);
      if (!pdfRes.ok) throw new Error('Gagal memuat dokumen PDF Surat Pesanan');
      const blob = await pdfRes.blob();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      showToast(`Nomor SP ${spNumber} berhasil diverifikasi dan siap dicetak`, 'success');

    } catch (e) {
      setShowModalPdf(false);
      showToast('Gagal memproses cetak ulang SP: ' + e.message, 'danger');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // 7. Tombol KELUAR -> Kembali ke modul SuratPesananApotek
  const handleKeluar = () => {
    navigate('/SuratPesananApotek');
  };

  // Filter supplier modal list
  const filteredSuppliers = supplierList.filter(s =>
    (s.nmsp || '').toLowerCase().includes(searchSupplier.toLowerCase()) ||
    (s.kdsp || '').toLowerCase().includes(searchSupplier.toLowerCase())
  );

  return (
    <div style={{
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      background: '#e2e8f0',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Toast Alert */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: 14,
          right: 14,
          zIndex: 9999,
          padding: '10px 18px',
          borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          background: toast.variant === 'danger' ? '#ef4444' : toast.variant === 'warning' ? '#f59e0b' : '#10b981',
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.84rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <i className={`bi ${toast.variant === 'danger' ? 'bi-exclamation-octagon' : toast.variant === 'warning' ? 'bi-exclamation-triangle' : 'bi-check-circle'}`} />
          {toast.message}
        </div>
      )}

      {/* WINDOW TITLE BAR */}
      <div style={{
        background: 'linear-gradient(90deg, #1e3a8a 0%, #0f172a 100%)',
        color: '#fff',
        padding: '6px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid #3b82f6',
        boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/logo.png"
            alt="Logo RS"
            style={{
              width: 32,
              height: 32,
              objectFit: 'contain',
              background: '#fff',
              borderRadius: 6,
              padding: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 800, fontSize: '0.98rem', letterSpacing: '0.6px' }}>
                PESANAN OBAT & AKSP
              </span>
              <Pill bg="rgba(56,189,248,0.2)" color="#7dd3fc" outline>
                {lokasi === '00001' ? 'Apotek (00001)' : 'Farmasi (00002)'}
              </Pill>
            </div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.2 }}>
              <span style={{ color: '#e2e8f0', fontWeight: 700 }}>RS MARY CILEUNGSI HIJAU</span> &bull; Jl. Raya Narogong KM 21 Cileungsi Bogor &bull; Telp. (021) 8249-2222 Fax. (021) 8249-3902
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleKeluar}
            title="Tutup form dan kembali ke Surat Pesanan Apotek"
            style={{
              background: '#ef4444',
              border: 'none',
              borderRadius: 4,
              color: '#fff',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          >
            &times;
          </button>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div style={{ padding: '8px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>

        {/* SECTION 1: PEMASOK & COUNTER METRICS & ACTIONS (Mirip Persis Gambas Screenshot) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 3.2fr) minmax(360px, 3.8fr) minmax(280px, 3fr)',
          gap: 6,
          alignItems: 'stretch'
        }}>
          {/* 1A. KOTAK PEMASOK */}
          <div style={{
            border: `1px solid ${C.borderD}`,
            background: '#f8fafc',
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              background: '#1e3a8a',
              color: '#fff',
              fontWeight: 800,
              fontSize: '0.75rem',
              padding: '3px 8px',
              textAlign: 'center',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ margin: '0 auto' }}>PEMASOK</span>
              {supplierList.length > 0 && (
                <span style={{ fontSize: '0.62rem', fontWeight: 600, opacity: 0.85 }}>
                  {supplierList.length} Pemasok
                </span>
              )}
            </div>
            <div style={{ padding: '4px 6px', display: 'flex', gap: 4, alignItems: 'center', flex: 1 }}>
              {/* Kotak Kuning Kode Suplier (txtkdsuplier) */}
              <input
                ref={inputKdSpRef}
                type="text"
                value={inputKdSp}
                onChange={(e) => setInputKdSp(e.target.value)}
                onKeyDown={handleKdSpKeyDown}
                placeholder="KDSP"
                title="Ketik kode atau inisial pemasok & tekan Enter untuk mencari (Esc untuk kembali)"
                style={{
                  width: '75px',
                  background: C.yellowInput,
                  border: '1px solid #ca8a04',
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: '0.76rem',
                  padding: '3px 6px',
                  textAlign: 'center',
                  outline: 'none',
                  color: '#0f172a'
                }}
              />

              {/* Kotak Putih Nama Suplier dengan Autocomplete Datalist (txtnmsuplier) */}
              <input
                type="text"
                list="daftar-suplier-datalist"
                value={selectedNmSp}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedNmSp(val);
                  // Cek apakah persis cocok dengan salah satu nama atau [kode] nama
                  const match = supplierList.find(s =>
                    s.nmsp === val ||
                    (s.kdsp && `[${s.kdsp}] ${s.nmsp}` === val)
                  );
                  if (match) {
                    selectSupplier(match);
                  }
                }}
                placeholder={loadingSuppliers ? "Memuat pemasok dari daftar_suplier_sp..." : "-- Ketik / Pilih Pemasok Obat (dari daftar_suplier_sp) --"}
                title="Ketik nama pemasok atau pilih dari daftar autocomplete"
                style={{
                  flex: 1,
                  background: '#fff',
                  border: `1px solid ${C.borderD}`,
                  borderRadius: 3,
                  fontWeight: 600,
                  fontSize: '0.76rem',
                  padding: '3px 6px',
                  outline: 'none',
                  color: '#0f172a'
                }}
              />

              {/* Datalist Sumber Data Pemasok dari daftar_suplier_sp */}
              <datalist id="daftar-suplier-datalist">
                {supplierList.map((s, idx) => (
                  <option key={idx} value={s.nmsp}>
                    {s.kdsp ? `[Kode: ${s.kdsp}] ` : ''}{s.total_item ? `(${s.total_item} item obat)` : ''}
                  </option>
                ))}
              </datalist>

              {/* Tombol Buka Modal Pencarian Lengkap (FrmQuery) */}
              <button
                onClick={() => {
                  setSearchSupplier(inputKdSp || '');
                  setShowSupplierModal(true);
                }}
                title="Buka Windows Dialog Pemilihan Pemasok (FrmQuery)"
                style={{
                  background: '#2563eb',
                  border: 'none',
                  borderRadius: 3,
                  color: '#fff',
                  padding: '4px 8px',
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3
                }}
              >
                <i className="bi bi-search" />
              </button>

              {/* Tombol Reload Data Suplier */}
              <button
                onClick={() => fetchSuppliers()}
                title="Refresh Daftar Pemasok dari daftar_suplier_sp"
                style={{
                  background: '#f1f5f9',
                  border: `1px solid ${C.borderD}`,
                  borderRadius: 3,
                  color: '#475569',
                  padding: '4px 6px',
                  fontSize: '0.74rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <i className={`bi bi-arrow-clockwise ${loadingSuppliers ? 'spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* 1B. COUNTERS: MINIMAL | PESANAN | KURANG */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 4
          }}>
            {/* Box MINIMAL */}
            <div style={{
              border: `1px solid ${C.borderD}`,
              background: '#f8fafc',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                background: '#1e3a8a',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.72rem',
                padding: '3px 4px',
                textAlign: 'center'
              }}>
                MINIMAL
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                padding: '4px',
                fontWeight: 800,
                fontSize: '0.94rem',
                color: '#1e293b'
              }}>
                {fmtNum(minOrder)}
              </div>
            </div>

            {/* Box PESANAN */}
            <div style={{
              border: `1px solid ${C.borderD}`,
              background: '#f8fafc',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                background: '#1e3a8a',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.72rem',
                padding: '3px 4px',
                textAlign: 'center'
              }}>
                PESANAN
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                padding: '4px',
                fontWeight: 800,
                fontSize: '0.94rem',
                color: '#0369a1'
              }}>
                {fmtNum(totalPesanan)}
              </div>
            </div>

            {/* Box KURANG */}
            <div style={{
              border: `1px solid ${C.borderD}`,
              background: '#f8fafc',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                background: '#1e3a8a',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.72rem',
                padding: '3px 4px',
                textAlign: 'center'
              }}>
                KURANG
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                padding: '4px',
                fontWeight: 800,
                fontSize: '0.94rem',
                color: kurangOrder > 0 ? '#dc2626' : '#10b981'
              }}>
                {fmtNum(kurangOrder)}
              </div>
            </div>
          </div>

          {/* 1C. ACTION HEADER: DIKIRIM | DRAFT | PROSES */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr 1fr',
            gap: 4
          }}>
            {/* Box DIKIRIM */}
            <div style={{
              border: '1px solid #991b1b',
              background: '#f8fafc',
              borderRadius: 4,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                background: '#991b1b',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.72rem',
                padding: '3px 4px',
                textAlign: 'center'
              }}>
                DIKIRIM
              </div>
              <div style={{ padding: '2px 4px', flex: 1, display: 'flex', alignItems: 'center', background: '#fff' }}>
                <input
                  ref={inputTglKirimRef}
                  type="text"
                  value={tglKirim}
                  onChange={(e) => setTglKirim(e.target.value)}
                  placeholder="DD/MM/YYYY"
                  style={{
                    width: '100%',
                    border: 'none',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.76rem',
                    color: '#0f172a',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Tombol DRAFT (Red button) */}
            <button
              onClick={handlePrintDraft}
              disabled={isGeneratingPdf || items.length === 0}
              style={{
                background: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)',
                border: '1px solid #b91c1c',
                borderRadius: 4,
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.82rem',
                cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                opacity: items.length === 0 ? 0.6 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <span style={{ letterSpacing: '0.5px' }}>DRAFT</span>
            </button>

            {/* Tombol PROSES (Blue button) */}
            <button
              onClick={handleOpenConfirmProses}
              disabled={isProcessingSp || items.length === 0}
              style={{
                background: 'linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)',
                border: '1px solid #1e40af',
                borderRadius: 4,
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.82rem',
                cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                opacity: items.length === 0 ? 0.6 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              <span style={{ letterSpacing: '0.5px' }}>PROSES</span>
            </button>
          </div>
        </div>

        {/* SECTION 2: HEADER BANNER ITEM OBAT (Toska Header sesuai Gambas) */}
        <div style={{
          background: '#0e7490',
          color: '#fff',
          borderRadius: 4,
          padding: '4px 8px',
          display: 'grid',
          gridTemplateColumns: 'minmax(240px, 2.5fr) minmax(180px, 1.8fr) minmax(400px, 4fr)',
          gap: 6,
          alignItems: 'center',
          fontSize: '0.74rem',
          fontWeight: 800,
          letterSpacing: '0.4px',
          border: '1px solid #0891b2'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-capsule" />
            <span>NAMA OBAT - AKSP</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-building" />
            <span>PRINSIPAL</span>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 3,
            padding: '2px 8px',
            textAlign: 'center'
          }}>
            PEMESANAN (MIN BELI &bull; PEMBELIAN &bull; PRICE LIST &bull; DISC &bull; HARGA JADI)
          </div>
        </div>

        {/* SECTION 3: TABEL GRID DATA OBAT PER PEMASOK */}
        <div style={{
          background: '#fff',
          borderRadius: 4,
          border: `1px solid ${C.borderD}`,
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 235px)' }}>
            <Table hover responsive style={{ margin: 0, fontSize: '0.74rem' }}>
              <thead style={{
                position: 'sticky',
                top: 0,
                background: '#f1f5f9',
                borderBottom: `2px solid ${C.border}`,
                zIndex: 2
              }}>
                <tr style={{ color: '#334155', fontWeight: 700 }}>
                  <th style={{ width: 38, textAlign: 'center' }}>No</th>
                  <th style={{ width: 85 }}>Kode</th>
                  <th style={{ minWidth: 220 }}>Nama Obat & AKSP</th>
                  <th style={{ width: 140 }}>Prinsipal</th>
                  <th style={{ width: 75, textAlign: 'right' }}>Min Order</th>
                  <th style={{ width: 75, textAlign: 'right', background: '#fef3c7' }}>Qty Beli</th>
                  <th style={{ width: 75, textAlign: 'center' }}>Sat Beli</th>
                  <th style={{ width: 65, textAlign: 'right' }}>Isi/Konv</th>
                  <th style={{ width: 75, textAlign: 'center' }}>Sat Jual</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Price List</th>
                  <th style={{ width: 60, textAlign: 'right' }}>Disc %</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Disc (Rp)</th>
                  <th style={{ width: 95, textAlign: 'right' }}>Harga Jadi</th>
                  <th style={{ width: 105, textAlign: 'right', background: '#e0f2fe' }}>Total (Rp)</th>
                </tr>
              </thead>
              <tbody>
                {loadingItems ? (
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center', padding: '40px 0' }}>
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span style={{ marginLeft: 8, color: '#64748b', fontWeight: 600 }}>
                        Memuat data pesanan supplier...
                      </span>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
                      <div style={{ fontSize: '1.8rem', color: '#94a3b8', marginBottom: 8 }}>
                        <i className="bi bi-inbox" />
                      </div>
                      <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.88rem' }}>
                        {selectedKdSp ? 'Tidak ada item pesanan obat untuk pemasok ini.' : 'Silakan Pilih Pemasok / Distributor terlebih dahulu.'}
                      </div>
                      <div style={{ fontSize: '0.74rem', marginTop: 4 }}>
                        {selectedKdSp ? 'Seluruh pesanan pemasok ini mungkin sudah diproses atau bernilai 0.' : 'Klik kolom nama pemasok di kiri atas untuk memilih supplier.'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((row, idx) => {
                    const isEven = idx % 2 === 1;
                    const totRow = floatSafe(row.total) || (floatSafe(row.qty || row.qtybeli) * floatSafe(row.hrgjadi));
                    return (
                      <tr
                        key={row.kd_obat || idx}
                        style={{
                          background: isEven ? '#f8fafc' : '#ffffff',
                          verticalAlign: 'middle'
                        }}
                      >
                        <td style={{ textAlign: 'center', color: '#64748b', fontWeight: 600 }}>{idx + 1}</td>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.kd_obat}</td>
                        <td style={{ fontWeight: 700, color: '#0f172a' }}>{row.nm_obat}</td>
                        <td style={{ color: '#475569' }}>{row.prinsipal || '-'}</td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>{fmtNum(row.minorder)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#1e40af', background: '#fffbeb' }}>
                          {fmtNum(row.qtybeli || row.qty)}
                        </td>
                        <td style={{ textAlign: 'center', color: '#475569' }}>{row.satuanbeli || '-'}</td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>{fmtNum(row.konversi || 1)}</td>
                        <td style={{ textAlign: 'center', color: '#475569' }}>{row.satuanjual || '-'}</td>
                        <td style={{ textAlign: 'right', color: '#334155' }}>{fmtNum(row.pricelist)}</td>
                        <td style={{ textAlign: 'right', color: '#334155' }}>{floatSafe(row.disc) > 0 ? `${row.disc}%` : '0%'}</td>
                        <td style={{ textAlign: 'right', color: '#334155' }}>{fmtNum(row.discrp)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{fmtNum(row.hrgjadi)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#0369a1', background: '#f0f9ff' }}>
                          {fmtNum(totRow)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {/* TABLE FOOTER / SUMMARY BAR */}
          <div style={{
            background: '#f8fafc',
            borderTop: `1px solid ${C.border}`,
            padding: '5px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.76rem',
            color: '#475569'
          }}>
            <div>
              Total Item: <strong>{items.length}</strong> jenis obat
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                Total Pesanan: <strong style={{ color: '#0369a1', fontSize: '0.86rem' }}>{fmt(totalPesanan)}</strong>
              </div>
              {minOrder > 0 && (
                <div>
                  Status Min. Order: {' '}
                  {kurangOrder > 0 ? (
                    <span style={{ color: '#dc2626', fontWeight: 700 }}>
                      Kurang {fmt(kurangOrder)}
                    </span>
                  ) : (
                    <span style={{ color: '#10b981', fontWeight: 700 }}>
                      <i className="bi bi-check-circle-fill me-1" /> Terpenuhi
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 4: BOTTOM TOOLBAR (Cari di kiri, CETAK ULANG & KELUAR di kanan) */}
        <div style={{
          background: '#fff',
          borderRadius: 4,
          border: `1px solid ${C.borderD}`,
          padding: '6px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
        }}>
          {/* Sisi Kiri: Filter Obat */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 260 }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 320 }}>
              <i className="bi bi-search" style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                color: '#94a3b8', fontSize: '0.76rem'
              }} />
              <input
                type="text"
                placeholder="Filter obat / prinsipal..."
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '4px 8px 4px 28px',
                  borderRadius: 4,
                  border: `1px solid ${C.border}`,
                  fontSize: '0.76rem',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Sisi Kanan: CETAK ULANG & KELUAR */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Tombol CETAK ULANG */}
            <button
              onClick={openCetakUlangModal}
              title="Cetak Ulang Surat Pesanan (SP)"
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
                border: `1px solid ${C.borderD}`,
                borderRadius: 4,
                padding: '4px 12px',
                color: '#334155',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <i className="bi bi-printer text-primary" />
              <span>CETAK ULANG</span>
            </button>

            {/* Tombol KELUAR -> Kembali ke SuratPesananApotek */}
            <button
              onClick={handleKeluar}
              title="Kembali ke Modul Surat Pesanan Apotek"
              style={{
                background: 'linear-gradient(180deg, #fee2e2 0%, #fecaca 100%)',
                border: '1px solid #f87171',
                borderRadius: 4,
                padding: '4px 14px',
                color: '#991b1b',
                fontWeight: 800,
                fontSize: '0.78rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <i className="bi bi-x-circle-fill text-danger" />
              <span>KELUAR</span>
            </button>
          </div>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* MODAL 1: PILIH PEMASOK / DISTRIBUTOR DARI TMPSP */}
      {/* ========================================================================= */}
      <Modal
        show={showSupplierModal}
        onHide={() => setShowSupplierModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ background: '#1e3a8a', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-building" />
            <span>Pilih Pemasok / Distributor Obat</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.85, background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 10 }}>
              {filteredSuppliers.length} dari {supplierList.length} Pemasok (daftar_suplier_sp)
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 12 }}>
          <div style={{ marginBottom: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                placeholder="Cari kode atau nama supplier dari daftar_suplier_sp..."
                value={searchSupplier}
                onChange={(e) => setSearchSupplier(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '6px 30px 6px 10px',
                  borderRadius: 4,
                  border: `1px solid ${C.borderD}`,
                  fontSize: '0.8rem',
                  outline: 'none'
                }}
              />
              {searchSupplier && (
                <button
                  onClick={() => setSearchSupplier('')}
                  title="Hapus filter pencarian"
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  <i className="bi bi-x-circle-fill" />
                </button>
              )}
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchSuppliers()}
              disabled={loadingSuppliers}
              title="Refresh data dari endpoint daftar_suplier_sp"
              style={{ fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <i className={`bi bi-arrow-clockwise ${loadingSuppliers ? 'spin' : ''}`} />
              <span>Reload</span>
            </Button>
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {loadingSuppliers ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b' }}>
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Mengambil data pemasok dari daftar_suplier_sp...</span>
              </div>
            ) : (
              <Table hover size="sm" style={{ fontSize: '0.76rem', margin: 0 }}>
                <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 75 }}>Kode</th>
                    <th>Nama Pemasok / Distributor</th>
                    <th style={{ width: 70, textAlign: 'center' }}>Item</th>
                    <th style={{ width: 105, textAlign: 'right' }}>Min Order</th>
                    <th style={{ width: 115, textAlign: 'right' }}>Total Pesanan</th>
                    <th style={{ width: 105, textAlign: 'right' }}>Kurang</th>
                    <th style={{ width: 70, textAlign: 'center' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '24px 0', color: '#64748b' }}>
                        Tidak ada pemasok yang cocok dengan pencarian "{searchSupplier}"
                      </td>
                    </tr>
                  ) : (
                    filteredSuppliers.map((s, idx) => (
                      <tr
                        key={idx}
                        style={{
                          cursor: 'pointer',
                          background: (selectedNmSp === s.nmsp || (selectedKdSp && selectedKdSp === s.kdsp)) ? '#eff6ff' : '#fff'
                        }}
                        onClick={() => selectSupplier(s)}
                      >
                        <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e40af' }}>
                          {s.kdsp || '-'}
                        </td>
                        <td style={{ fontWeight: 600, color: '#0f172a' }}>{s.nmsp}</td>
                        <td style={{ textAlign: 'center' }}>
                          <Pill bg="#e2e8f0" color="#334155">{s.total_item}</Pill>
                        </td>
                        <td style={{ textAlign: 'right', color: '#64748b' }}>{fmtNum(s.minorder)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                          {fmtNum(s.total_pesanan)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: s.kurang > 0 ? '#dc2626' : '#16a34a' }}>
                          {fmtNum(s.kurang)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Button size="sm" variant="primary" style={{ fontSize: '0.68rem', padding: '1px 8px' }}>
                            Pilih
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 12px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowSupplierModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL 2: KONFIRMASI PROSES SP RESMI */}
      {/* ========================================================================= */}
      <Modal
        show={showConfirmProses}
        onHide={() => setShowConfirmProses(false)}
        centered
      >
        <Modal.Header closeButton style={{ background: '#1e40af', color: '#fff', padding: '10px 14px' }}>
          <Modal.Title style={{ fontSize: '0.94rem', fontWeight: 800 }}>
            <i className="bi bi-question-circle-fill me-2" />
            Konfirmasi Simpan & Proses Surat Pesanan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '16px 20px', fontSize: '0.84rem' }}>
          <p style={{ fontWeight: 700, fontSize: '0.96rem', color: '#0f172a', marginBottom: 12 }}>
            Data Surat Pesanan akan disimpan dan di proses sebagai SP resmi?
          </p>

          <div style={{
            background: '#f8fafc',
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 12
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 6, fontSize: '0.78rem' }}>
              <span style={{ color: '#64748b' }}>Pemasok:</span>
              <strong>{selectedNmSp} ({selectedKdSp})</strong>

              <span style={{ color: '#64748b' }}>Tanggal Kirim:</span>
              <strong>{tglKirim}</strong>

              <span style={{ color: '#64748b' }}>Jumlah Obat:</span>
              <strong>{items.length} item</strong>

              <span style={{ color: '#64748b' }}>Total Nilai:</span>
              <strong style={{ color: '#0369a1', fontSize: '0.86rem' }}>{fmt(totalPesanan)}</strong>
            </div>
          </div>

          {kurangOrder > 0 && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              padding: '8px 12px',
              color: '#991b1b',
              fontSize: '0.76rem',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '1rem' }} />
              <div>
                <strong>Peringatan Minimal Order:</strong> Pesanan masih kurang <strong>{fmt(kurangOrder)}</strong> dari ketentuan minimal order distributor ({fmt(minOrder)}).
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '8px 14px', background: '#f8fafc' }}>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowConfirmProses(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleExecuteProses}
            style={{ fontWeight: 700, padding: '4px 16px' }}
          >
            <i className="bi bi-check-lg me-1" /> Ya, Simpan & Proses SP
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL 3: CETAK ULANG SURAT PESANAN (RIWAYAT HDSP) */}
      {/* ========================================================================= */}
      <Modal
        show={showCetakUlangModal}
        onHide={() => setShowCetakUlangModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton style={{ background: '#0f172a', color: '#fff', padding: '10px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 800 }}>
            <i className="bi bi-printer-fill me-2 text-info" />
            Cetak Ulang Surat Pesanan (SP)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 12 }}>
          {/* Box Masukan Nomor Surat Pesanan yang akan dicetak (txtnospulang) */}
          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            padding: '10px 14px',
            borderRadius: 6,
            marginBottom: 12
          }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e3a8a', marginBottom: 6, display: 'block' }}>
              <i className="bi bi-input-cursor me-1" /> Masukan Nomor Surat Pesanan yang akan di Cetak :
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                autoFocus
                placeholder="Ketik Nomor SP (contoh: 00001/SP/12/2019/14)"
                value={inputNoSpUlang}
                onChange={(e) => setInputNoSpUlang(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && inputNoSpUlang.trim()) {
                    handleCetakSpByNo(inputNoSpUlang.trim());
                  }
                }}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: 4,
                  border: '1px solid #93c5fd',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  outline: 'none',
                  background: '#fff'
                }}
              />
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleCetakSpByNo(inputNoSpUlang.trim())}
                disabled={!inputNoSpUlang.trim()}
                style={{ fontSize: '0.8rem', fontWeight: 700, padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <i className="bi bi-printer-fill" />
                <span>Proses Cetak SP</span>
              </Button>
            </div>
            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
              * Tekan <b>Enter</b> atau klik <b>Proses Cetak SP</b> untuk memverifikasi ke database HDSP dan menampilkan dokumen SP resmi.
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Filter riwayat nomor SP atau nama distributor..."
              value={searchHistory}
              onChange={(e) => setSearchHistory(e.target.value)}
              style={{
                width: '100%',
                padding: '4px 8px',
                borderRadius: 4,
                border: `1px solid ${C.border}`,
                fontSize: '0.76rem'
              }}
            />
          </div>

          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            <Table hover size="sm" style={{ fontSize: '0.74rem', margin: 0 }}>
              <thead style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ width: 160 }}>Nomor SP</th>
                  <th style={{ width: 90 }}>Tanggal</th>
                  <th>Nama Distributor</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Total (Rp)</th>
                  <th style={{ width: 75, textAlign: 'center' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loadingHistory ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '20px 0' }}>
                      <Spinner animation="border" size="sm" />
                    </td>
                  </tr>
                ) : historySpList.filter(h =>
                  (h.no_sp || '').toLowerCase().includes(searchHistory.toLowerCase()) ||
                  (h.nm_suplier || '').toLowerCase().includes(searchHistory.toLowerCase())
                ).length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '20px 0', color: '#64748b' }}>
                      Tidak ada riwayat Surat Pesanan
                    </td>
                  </tr>
                ) : (
                  historySpList
                    .filter(h =>
                      (h.no_sp || '').toLowerCase().includes(searchHistory.toLowerCase()) ||
                      (h.nm_suplier || '').toLowerCase().includes(searchHistory.toLowerCase())
                    )
                    .map((row, idx) => (
                      <tr key={row.no_sp || idx}>
                        <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e40af' }}>
                          {row.no_sp}
                        </td>
                        <td>{row.tanggal}</td>
                        <td style={{ fontWeight: 600 }}>{row.nm_suplier || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                          {fmtNum(row.total)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleCetakSpByNo(row.no_sp)}
                            style={{ padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700 }}
                          >
                            <i className="bi bi-printer" />
                          </Button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '6px 12px' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowCetakUlangModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========================================================================= */}
      {/* MODAL 4: IN-APP PDF VIEWER (DRAFT & SP RESMI) */}
      {/* ========================================================================= */}
      <Modal
        show={showModalPdf}
        onHide={() => {
          setShowModalPdf(false);
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          setPdfUrl('');
        }}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton style={{ background: '#0f172a', color: '#fff', padding: '8px 14px' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-file-earmark-pdf-fill text-danger" />
            <span>{pdfTitle}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '78vh', background: '#334155' }}>
          {isGeneratingPdf || !pdfUrl ? (
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff'
            }}>
              <Spinner animation="border" variant="info" style={{ width: '2.5rem', height: '2.5rem' }} />
              <div style={{ marginTop: 14, fontWeight: 700, fontSize: '0.95rem' }}>
                Merender Dokumen PDF dari Server...
              </div>
              <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 4 }}>
                Harap tunggu sebentar selagi format cetak disiapkan
              </div>
            </div>
          ) : (
            <iframe
              src={pdfUrl}
              title="Pratinjau Surat Pesanan"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{
          padding: '6px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          background: '#f8fafc'
        }}>
          <div style={{ fontSize: '0.74rem', color: '#64748b' }}>
            RS Mary Cileungsi Hijau &bull; Dokumen Pengadaan Farmasi
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={pdfFilename}
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: '0.76rem', fontWeight: 600 }}
              >
                <i className="bi bi-download me-1" /> Unduh PDF
              </a>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowModalPdf(false);
                if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }}
              style={{ fontSize: '0.76rem' }}
            >
              Tutup
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

// Helper safely parse float
const floatSafe = (v) => {
  if (!v) return 0;
  const num = parseFloat(v);
  return isNaN(num) ? 0 : num;
};

export default ProsesPesananApotek;
